import { useState, useMemo, useCallback, useEffect } from 'react';

/**
 * Column visibility state for Qawaem list tables — pairs with QawaemTableHeaderActionsCell.
 * @param {Array<{ key: string, label: string, required?: boolean }>} baseColumns - data columns (no actions)
 */
export function useQawaemTableColumnVisibility(baseColumns = []) {
  const [columnVisibility, setColumnVisibility] = useState(() =>
    Object.fromEntries(baseColumns.map((c) => [c.key, true])),
  );

  useEffect(() => {
    setColumnVisibility((prev) => {
      const next = { ...prev };
      let changed = false;
      baseColumns.forEach((col) => {
        if (col.key === 'actions') return;
        if (!(col.key in next)) {
          next[col.key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [baseColumns]);

  const handleToggleColumn = useCallback(
    (key) => {
      const col = baseColumns.find((c) => c.key === key);
      if (col?.required || key === 'actions') return;
      setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
    },
    [baseColumns],
  );

  const showCol = useCallback(
    (key) => columnVisibility[key] !== false,
    [columnVisibility],
  );

  const pickerColumns = useMemo(() => {
    const dataCols = baseColumns.map((col) => ({
      ...col,
      checked: columnVisibility[col.key] !== false,
    }));
    return dataCols;
  }, [baseColumns, columnVisibility]);

  const visibleColumnCount = pickerColumns.filter((c) => c.checked !== false).length;

  return {
    columnVisibility,
    handleToggleColumn,
    showCol,
    pickerColumns,
    visibleColumnCount,
  };
}

/** Build picker column list with actions entry appended. */
export function withQawaemActionsColumn(pickerColumns, actionsLabel) {
  return [
    ...pickerColumns,
    { key: 'actions', label: actionsLabel, checked: true, required: true },
  ];
}

/** Count visible table columns (checkbox + optional extras + actions). */
export function countVisibleTableCols(baseColumns, showCol, { extra = 0 } = {}) {
  let n = 1;
  baseColumns.forEach((col) => { if (showCol(col.key)) n += 1; });
  return n + extra + 1;
}
